import { computed } from "vue";
import { useConversation } from "../../../../Stores/Conversation";
import {
    filterGroups,
    filterPeople,
    useNewDirectoryData,
} from "./useNewDirectoryData";
import { countMembershipPanelSearchResults } from "./workPanelMembershipSearch";

export function getConversationMembers(activeConversation) {
    if (!activeConversation) {
        return [];
    }

    const isArchived =
        activeConversation.is_archived ||
        activeConversation.isArchived?.();

    const participants = isArchived
        ? (activeConversation.getParticipants?.() ?? [])
        : (activeConversation.getActiveParticipats?.() ??
           activeConversation.getParticipants?.() ??
           []);

    return participants.filter((participant) => !participant?.is_agent);
}

export function getLiveConversationParticipantIds(activeConversation) {
    const ids = new Set();

    for (const participant of getConversationMembers(activeConversation)) {
        if (participant?.id != null) {
            ids.add(Number(participant.id));
        }
    }

    return ids;
}

export function getConversationMemberOrderIds(activeConversation) {
    if (!activeConversation) {
        return [];
    }

    return (activeConversation.getParticipants?.() ?? [])
        .filter((participant) => !participant?.is_agent)
        .map((participant) => participant.id);
}

export function buildMembersTabPeople({
    conversationMembers,
    locallyAddedMemberIds,
    locallyAddedMemberProfiles = [],
    profiles,
    userStore,
}) {
    const conversationMemberById = new Map(
        conversationMembers.map((member) => [member.id, member]),
    );
    const profileById = new Map(profiles.map((profile) => [profile.id, profile]));
    const locallyAddedProfileById = new Map(
        locallyAddedMemberProfiles.map((profile) => [profile.id, profile]),
    );
    const orderedIds = [
        ...conversationMembers.map((member) => member.id),
        ...locallyAddedMemberIds.filter((id) => !conversationMemberById.has(id)),
    ];
    const seen = new Set();

    return orderedIds
        .filter((id) => {
            if (seen.has(id)) {
                return false;
            }

            seen.add(id);

            return true;
        })
        .map((id) => {
            const profile = userStore.getUserProfileById(id);

            if (profile?.is_agent) {
                return null;
            }

            return (
                conversationMemberById.get(id) ??
                locallyAddedProfileById.get(id) ??
                profileById.get(id) ??
                profile ??
                null
            );
        })
        .filter(Boolean);
}

export function mergeWorkPanelMemberWithStoreProfile(member, storeProfile) {
    if (!member || !storeProfile) {
        return member;
    }

    return {
        ...member,
        email: member.email ?? storeProfile.email,
        full_name: member.full_name ?? storeProfile.full_name,
        avatar_url: member.avatar_url ?? storeProfile.avatar_url,
        is_organization_user:
            storeProfile.is_organization_user ?? member.is_organization_user,
        non_registered: storeProfile.non_registered ?? member.non_registered,
        is_active: storeProfile.is_active ?? member.is_active,
    };
}

export function mergeParticipantsWithProfiles(participants, profiles) {
    const profileById = new Map(profiles.map((profile) => [profile.id, profile]));

    return participants.map((participant) => {
        const profile = profileById.get(participant.id);

        if (!profile) {
            return participant;
        }

        return {
            ...profile,
            ...participant,
            is_active: profile.is_active,
            is_organization_user: profile.is_organization_user,
            non_registered: profile.non_registered,
            full_name: participant.full_name ?? profile.full_name,
            email: participant.email ?? profile.email,
            avatar_url: participant.avatar_url ?? profile.avatar_url,
        };
    });
}

export function useNewMembershipData(search) {
    const conversationStore = useConversation();

    const activeConversation = computed(() => conversationStore.active);

    const isTopicConversation = computed(() =>
        Boolean(activeConversation.value?.is_group_one_off),
    );

    const searchQuery = computed(() => search.value?.trim() ?? "");

    const {
        profiles,
        groups,
        topics,
        associatedPeople,
        disassociatedPeople,
        registeredPeople,
        pendingInvites,
        canShowInvites,
        filteredAssociatedPeople,
        filteredDisassociatedPeople,
        searchResultCount: directorySearchResultCount,
        searchResultText: directorySearchResultText,
    } = useNewDirectoryData(search);

    const conversationMembers = computed(() =>
        mergeParticipantsWithProfiles(
            getConversationMembers(activeConversation.value),
            profiles.value,
        ),
    );

    const memberIds = computed(() =>
        conversationMembers.value.map((member) => member.id),
    );

    const allSelectableGroups = computed(() => [
        ...groups.value,
        ...topics.value,
    ]);

    const availablePeople = computed(() =>
        registeredPeople.value.filter(
            (profile) => !memberIds.value.includes(profile.id),
        ),
    );

    const filteredMembers = computed(() =>
        filterPeople(conversationMembers.value, searchQuery.value),
    );

    const filteredAvailablePeople = computed(() =>
        filterPeople(availablePeople.value, searchQuery.value),
    );

    const filteredGroups = computed(() =>
        filterGroups(groups.value, searchQuery.value),
    );

    const filteredTopics = computed(() =>
        filterGroups(topics.value, searchQuery.value),
    );

    const filteredPendingInvites = computed(() =>
        filterPeople(pendingInvites.value, searchQuery.value),
    );

    const allTabPeople = computed(() => [
        ...associatedPeople.value,
        ...(canShowInvites.value ? pendingInvites.value : []),
        ...disassociatedPeople.value,
    ]);

    const filteredAllTabPeople = computed(() => [
        ...filteredAssociatedPeople.value,
        ...(canShowInvites.value ? filteredPendingInvites.value : []),
        ...filteredDisassociatedPeople.value,
    ]);

    const searchResultCount = computed(() =>
        countMembershipPanelSearchResults({
            associatedPeople: associatedPeople.value,
            disassociatedPeople: disassociatedPeople.value,
            pendingInvites: pendingInvites.value,
            canShowInvites: canShowInvites.value,
            searchQuery: searchQuery.value,
        }),
    );

    const searchResultText = computed(() => {
        if (!searchQuery.value) {
            return directorySearchResultText.value;
        }

        const count = searchResultCount.value;

        return `${count} result${count !== 1 ? "s" : ""}`;
    });

    return {
        activeConversation,
        isTopicConversation,
        conversationMembers,
        memberIds,
        profiles,
        groups,
        topics,
        allSelectableGroups,
        registeredPeople,
        availablePeople,
        associatedPeople,
        disassociatedPeople,
        pendingInvites,
        canShowInvites,
        filteredMembers,
        filteredAvailablePeople,
        allTabPeople,
        filteredAllTabPeople,
        filteredGroups,
        filteredTopics,
        filteredPendingInvites,
        searchQuery,
        searchResultCount,
        searchResultText,
        directorySearchResultCount,
    };
}
